import { useEffect, useMemo, useRef, useState } from 'react';
import { toast, ToastOptions } from 'react-toastify';

import { getHttpStatusFromError } from '@/queries/queryErrorHandling';
import { transientStatusToastContainerId } from '@/queries/toastContainers';
import { toastInfoOptions } from '@/utils/toastOptions';

export const transientEndpointErrorToastMessage =
  'Verbinding tijdelijk verstoord. We proberen de gegevens opnieuw op te halen.';

export type CriticalQueryEntry = {
  key: string;
  error: unknown;
  errorUpdatedAt: number;
  dataUpdatedAt: number;
};

type QueryErrorState = Omit<CriticalQueryEntry, 'key'>;

export const queryErrorEntry = (key: string, query: QueryErrorState): CriticalQueryEntry => ({
  key,
  error: query.error,
  errorUpdatedAt: query.errorUpdatedAt,
  dataUpdatedAt: query.dataUpdatedAt,
});

type DelayedContinuousQueryErrorOptions = {
  scope: string;
  criticalQueries: CriticalQueryEntry[];
  supplementalQueries?: CriticalQueryEntry[];
  gracePeriodMs?: number;
  toastId?: string;
  toastMessage?: string;
};

type ContinuousErrorTimer = { startedAt: number; lastSeenAt: number };

const continuousErrorTimerByKey = new Map<string, ContinuousErrorTimer>();

// Een timer die langer dan de grace period niet is bijgewerkt (bv. omdat de gebruiker weg was van de view) is verouderd
// en mag niet leiden tot een directe blocking error bij de eerste nieuwe fout.
const getActiveTimer = (scopedKey: string, now: number, gracePeriodMs: number) => {
  const timer = continuousErrorTimerByKey.get(scopedKey);
  return timer && now - timer.lastSeenAt <= gracePeriodMs ? timer : undefined;
};

const getScopedKey = (scope: string, queryKey: string) => `${scope}::${queryKey}`;

const isActiveContinuousError = (entry: CriticalQueryEntry) =>
  Boolean(entry.error) && entry.errorUpdatedAt > 0 && entry.errorUpdatedAt >= entry.dataUpdatedAt;

const isAuthError = (error: unknown) => {
  const status = getHttpStatusFromError(error);
  return status === 401 || status === 403;
};

const transientToastOptions: ToastOptions = {
  ...toastInfoOptions,
  autoClose: false,
  closeOnClick: false,
};

export const resetContinuousQueryErrorTimers = () => {
  continuousErrorTimerByKey.clear();
};

/**
 * Coordinates endpoint failures at page level so all queries share one toast and one grace period.
 * Queries that make the page unusable belong in `criticalQueries`; supplemental queries only trigger the toast.
 * Views opt in explicitly because the same query can be critical on one page and supplemental on another.
 */
export const useDelayedContinuousQueryError = ({
  scope,
  criticalQueries,
  supplementalQueries = [],
  gracePeriodMs = 60_000,
  toastId = `transient-endpoint-error-${scope}`,
  toastMessage = transientEndpointErrorToastMessage,
}: DelayedContinuousQueryErrorOptions) => {
  const [, setTick] = useState(0);
  const scopedKeysRef = useRef<Set<string>>(new Set());
  const now = Date.now();

  const trackedQueries = useMemo(
    () => [
      ...criticalQueries.map((entry) => ({ ...entry, blocksPage: true })),
      ...supplementalQueries.map((entry) => ({ ...entry, blocksPage: false })),
    ],
    [criticalQueries, supplementalQueries],
  );

  const trackedFailures = useMemo(
    () =>
      trackedQueries.map((entry) => {
        const scopedKey = getScopedKey(scope, entry.key);
        const activeError = isActiveContinuousError(entry);
        const startedAt =
          entry.blocksPage && activeError
            ? (getActiveTimer(scopedKey, now, gracePeriodMs)?.startedAt ?? entry.errorUpdatedAt ?? now)
            : null;

        return {
          ...entry,
          scopedKey,
          activeError,
          authError: activeError ? isAuthError(entry.error) : false,
          startedAt,
        };
      }),
    [now, scope, trackedQueries, gracePeriodMs],
  );

  useEffect(() => {
    const activeQueryKeys = new Set(trackedQueries.map((entry) => getScopedKey(scope, entry.key)));
    scopedKeysRef.current.forEach((scopedKey) => {
      if (!activeQueryKeys.has(scopedKey)) {
        continuousErrorTimerByKey.delete(scopedKey);
      }
    });
    scopedKeysRef.current = activeQueryKeys;

    trackedFailures.forEach((entry) => {
      if (!entry.blocksPage || !entry.activeError || entry.authError) {
        continuousErrorTimerByKey.delete(entry.scopedKey);
        return;
      }

      const timer = getActiveTimer(entry.scopedKey, now, gracePeriodMs);
      continuousErrorTimerByKey.set(entry.scopedKey, {
        startedAt: timer?.startedAt ?? entry.startedAt ?? now,
        lastSeenAt: now,
      });
    });
  }, [now, scope, trackedFailures, trackedQueries, gracePeriodMs]);

  const hasAuthBlockingError = trackedFailures.some(
    (entry) => entry.blocksPage && entry.activeError && entry.authError,
  );
  const hasDelayedBlockingError = trackedFailures.some(
    (entry) =>
      entry.blocksPage &&
      entry.activeError &&
      !entry.authError &&
      entry.startedAt !== null &&
      now - entry.startedAt >= gracePeriodMs,
  );
  const isBlockingError = hasAuthBlockingError || hasDelayedBlockingError;
  const hasTransientFailure =
    trackedFailures.some((entry) => entry.activeError && (!entry.authError || !entry.blocksPage)) && !isBlockingError;

  const nextTransitionInMs = trackedFailures.reduce<number | null>((shortest, entry) => {
    if (!entry.blocksPage || !entry.activeError || entry.authError || entry.startedAt === null) {
      return shortest;
    }
    const remaining = entry.startedAt + gracePeriodMs - now;
    if (remaining <= 0) {
      return shortest;
    }
    if (shortest === null || remaining < shortest) {
      return remaining;
    }
    return shortest;
  }, null);

  useEffect(() => {
    if (nextTransitionInMs === null) {
      return;
    }

    const timeoutId = window.setTimeout(() => setTick((prev) => prev + 1), nextTransitionInMs);
    return () => window.clearTimeout(timeoutId);
  }, [nextTransitionInMs]);

  useEffect(() => {
    if (hasTransientFailure) {
      if (!toast.isActive(toastId)) {
        toast.info(toastMessage, {
          ...transientToastOptions,
          toastId,
          containerId: transientStatusToastContainerId,
        });
      }
      return;
    }

    toast.dismiss(toastId);
    toast.clearWaitingQueue({ containerId: transientStatusToastContainerId });
  }, [hasTransientFailure, toastId, toastMessage]);

  useEffect(() => {
    return () => {
      toast.dismiss(toastId);
      toast.clearWaitingQueue({ containerId: transientStatusToastContainerId });
    };
  }, [toastId]);

  return {
    hasTransientFailure,
    isBlockingError,
  };
};
